import { Stack, Text, Center } from '@mantine/core';
import { Database } from 'lucide-react';
import { SurveyTabToolbar } from './SurveyTabToolbar';
import { SurveyTabTable } from './SurveyTabTable';
import { useDataLibrarySurveyResponses } from './useDataLibrarySurveyResponses';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';

interface DataLibrarySurveyTabProps {
  selectedCohortIds: string[];
  formTypes: string[];
  emptyMessage: string;
  noConfigMessage: string;
  entityLabel: string;
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
  /** MED-773: Initial filters when opened from drill-down with group selection */
  initialFilters?: ActiveFilterRow[];
}

export function DataLibrarySurveyTab({
  selectedCohortIds,
  formTypes,
  emptyMessage,
  noConfigMessage,
  entityLabel,
  activeGroup,
  onGroupChange,
  initialFilters,
}: DataLibrarySurveyTabProps) {
  const {
    rows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
    isMidVsEnd,
    groupLimitTotal,
  } = useDataLibrarySurveyResponses({ selectedCohortIds, formTypes, activeGroup, onGroupChange, initialFilters });

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Database size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view survey responses
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  // MDF-39: a grouped table holds every row, so it has no pages
  const hidePagination = !!groupedRows;

  return (
    <Stack gap="sm">
      <SurveyTabToolbar
        groupLimitTotal={groupLimitTotal}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filterOptions={filterOptions}
        activeQuickFilters={activeQuickFilters}
        onQuickFilterToggle={handleQuickFilterToggle}
        activeFilters={activeFilters}
        onFilterAdd={handleFilterAdd}
        onFilterChange={handleFilterChange}
        onFilterRemove={handleFilterRemove}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        sortConfig={sortConfig}
        onSortChange={handleSortPanelChange}
        activeGroup={activeGroup}
        onGroupChange={onGroupChange}
        columns={columns}
        onColumnsChange={setColumns}
        totalCount={totalCount}
        entityLabel={entityLabel}
        isMidVsEnd={isMidVsEnd}
        selectedCohortIds={selectedCohortIds}
        formTypes={formTypes}
      />

      <SurveyTabTable
        data={rows}
        averages={averages}
        groupedData={groupedRows}
        columns={visibleColumns}
        questions={questions}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters && hasSurveyConfig}
        onClearFilters={clearAllFilters}
        emptyMessage={!hasSurveyConfig ? noConfigMessage : emptyMessage}
        isMidVsEnd={isMidVsEnd}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hidePagination={hidePagination}
      />
    </Stack>
  );
}
